
































































































































































































































import { Vue, Component, Prop } from 'vue-property-decorator';
import moment from 'moment';
import { TreatmentEpisode, TreatmentEpisodeCancelRequest, TreatmentEpisodePaymentHistory } from '@/models/treatment-episode';
import { CANCEL_REASON_UNSELECTED, getCancelInitiatorLabel } from '@common/constants/auth';
import { PLAN_PAYMENT_TYPE_NAME } from '@common/constants/plan';
import { DeliveryStateDisplay } from '@common/enums/order';
import { getTreatmentStateLabel } from '@common/constants/treatmentStateLabels';

const EPISODE_END_STATE_LABEL: Record<string, string> = {
  canceled: 'キャンセル済み',
  doctor_canceled: '処方キャンセル済み',
  completed: '完了',
};

const EPISODE_END_STATE_COLOR: Record<string, string> = {
  canceled: 'red',
  doctor_canceled: 'volcano',
  completed: 'blue',
};

const FEEDBACK_TYPE_LABEL: Record<string, string> = {
  prescription: '処方提案',
  ng: '処方NG',
  cancel_prescription: '処方キャンセル',
  reserve_prescription: '処方保留',
};

const PAYMENT_COLUMNS = [
  { title: '配送回', dataIndex: 'delivery_sequence', key: 'delivery_sequence', width: 80 },
  {
    title: 'プラン',
    dataIndex: 'plan_name',
    key: 'plan_name',
    customRender: (val) => val || '—',
  },
  {
    title: '状態',
    dataIndex: 'state',
    key: 'state',
    width: 110,
    customRender: (val) => DeliveryStateDisplay[val] || val || '—',
  },
  { title: '金額', dataIndex: 'amount', key: 'amount', width: 100 },
  {
    title: '支払日',
    dataIndex: 'paid_at',
    key: 'paid_at',
    customRender: (val) => val ? moment(val).format('YYYY/MM/DD') : '—',
  },
  {
    title: '配送予定日',
    dataIndex: 'delivery_date',
    key: 'delivery_date',
    customRender: (val) => val ? moment(val).format('YYYY/MM/DD') : '—',
  },
];

@Component({})
export default class PatientTreatmentTimeline extends Vue {
  @Prop({ default: () => [] }) episodes!: TreatmentEpisode[];
  @Prop({ default: false }) isLoading!: boolean;
  @Prop({ default: true }) showEpisodeMeta!: boolean;

  private openKeys: string[] = [];

  private paymentColumns = PAYMENT_COLUMNS;

  private formatDate(value: string | null): string {
    if (!value) {
      return '—';
    }
    return moment(value).format('YYYY年MM月DD日');
  }

  private formatDateTime(value: string | null): string {
    if (!value) {
      return '—';
    }
    const m = moment(value);
    if (!m.isValid()) {
      return '—';
    }
    return m.format('YYYY年MM月DD日 HH:mm');
  }

  private formatReservationTime(rec: { reservation_date: string | null }): string {
    const date = rec.reservation_date;
    if (!date) {
      return '—';
    }
    const DAY_OF_WEEK = ['日', '月', '火', '水', '木', '金', '土'];
    const m = moment(date);
    if (!m.isValid()) {
      return '—';
    }
    const dow = DAY_OF_WEEK[m.day()];
    return m.format(`YYYY年MM月DD日 (${dow}) HH:mm`);
  }

  private feedbackTypeLabel(type: string | null): string {
    return FEEDBACK_TYPE_LABEL[type || ''] || type || 'フィードバック';
  }

  private planPaymentTypeName(paymentType: number): string {
    return PLAN_PAYMENT_TYPE_NAME[paymentType] || String(paymentType);
  }

  private planName(record: TreatmentEpisodePaymentHistory): string {
    return record.plan_name || '—';
  }

  private getStateLabel(state: string): string {
    return getTreatmentStateLabel(state);
  }

  private endStateLabel(endState: string | null): string {
    if (!endState) {
      return '終了';
    }
    return EPISODE_END_STATE_LABEL[endState] || endState;
  }

  /**
   * Both a patient-requested cancellation and one the clinic initiated close the
   * episode as 処方キャンセル済み, so the end state alone cannot tell them apart.
   */
  private cancelInitiatorLabel(episode: TreatmentEpisode): string {
    return getCancelInitiatorLabel(episode.cancel_initiator);
  }

  /** Requests filed before this feature shipped carry no reason at all. */
  private reasonLabel(request: TreatmentEpisodeCancelRequest): string {
    return request.reason_label || CANCEL_REASON_UNSELECTED;
  }

  private endStateColor(endState: string | null): string {
    return EPISODE_END_STATE_COLOR[endState || ''] || 'default';
  }

  private groupPaymentHistory(
    episode: TreatmentEpisode,
  ): Array<{ planName: string | null; rows: TreatmentEpisode['payment_history'] }> {
    const rows = episode.payment_history || [];
    if (!rows.length) {
      return [];
    }

    // Build a map of order_id → plan name from selected_plans
    const planNameByOrderId: Record<number, string> = {};
    (episode.selected_plans || []).forEach((sp) => {
      if (sp.order_id != null && sp.plan) {
        planNameByOrderId[sp.order_id] = sp.plan.name;
      }
    });

    // Count how many distinct order_ids appear in payment_history
    const orderIds = [...new Set(rows.map((r) => r.order_id).filter((id) => id != null))];

    // If all rows share 1 order (or none have order_id), render as a single flat table (backward-compat)
    if (orderIds.length <= 1) {
      return [{ planName: null, rows }];
    }

    // Group rows by order_id, preserving insertion order (API sends rows sorted by delivery_sequence)
    const groupMap: Map<number | null, TreatmentEpisode['payment_history']> = new Map();
    rows.forEach((row) => {
      const key = row.order_id ?? null;
      if (!groupMap.has(key)) {
        groupMap.set(key, []);
      }
      groupMap.get(key)!.push(row);
    });

    return [...groupMap.entries()].map(([orderId, groupRows]) => ({
      planName: orderId != null ? (planNameByOrderId[orderId] || null) : null,
      rows: groupRows,
    }));
  }
}
